import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Copy, Printer } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth-context";

export default function MfaSetup() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [setup, setSetup] = useState(null); // { qr_png_base64, secret_b32, otpauth_url }
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (user && user.mfa_enabled) {
      navigate(user.role === "admin" ? "/admin/patients" : "/dashboard", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/mfa/setup");
        setSetup(data);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/mfa/confirm", { code: code.trim() });
      setBackupCodes(data.backup_codes);
      toast.success("Two-factor authentication enabled.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const finish = async () => {
    await refresh();
    navigate(user?.role === "admin" ? "/admin/patients" : "/dashboard", { replace: true });
  };

  const copyAll = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join("\n")).then(() => toast.success("Backup codes copied."));
  };

  const printCodes = () => window.print();

  const input = "w-full rounded-sm border border-stone-300 bg-white px-4 py-3 text-sm font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-bronze-dark/40 focus:border-bronze-dark";

  if (loading) {
    return <div className="container-ccc py-16 text-ink-muted text-sm">Loading…</div>;
  }

  if (backupCodes) {
    return (
      <div data-testid="mfa-backup-codes-page" className="container-ccc py-16 max-w-xl">
        <div className="eyebrow">Step 2 of 2 · Save your backup codes</div>
        <h1 className="font-serif text-4xl mt-3">Two-factor is now active.</h1>
        <p className="mt-4 text-ink-muted">
          Save these <strong>8 one-time-use backup codes</strong> somewhere safe — a password manager, a printed copy in a locked drawer, or a secure note. If you ever lose your phone, you'll need one of these to log in.
        </p>
        <p className="mt-2 text-sm text-destructive">
          We will not show these codes again.
        </p>

        <div data-testid="mfa-backup-codes" className="mt-8 p-6 bg-cream-100 border border-bronze-light/40 rounded-md grid grid-cols-2 gap-3 font-mono text-base">
          {backupCodes.map((c) => (
            <code key={c} className="bg-white px-3 py-2 rounded-sm border border-stone-200/60 text-center">{c}</code>
          ))}
        </div>

        <div className="mt-6 flex gap-3 flex-wrap">
          <button onClick={copyAll} data-testid="mfa-copy-codes" className="btn-secondary inline-flex items-center gap-1.5">
            <Copy size={16}/> Copy all
          </button>
          <button onClick={printCodes} className="btn-secondary inline-flex items-center gap-1.5">
            <Printer size={16}/> Print
          </button>
        </div>

        <label className="mt-8 flex items-start gap-3 text-sm">
          <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="mfa-ack-checkbox" className="mt-1" />
          <span>I have saved these codes somewhere safe. I understand they will not be shown again.</span>
        </label>

        <button onClick={finish} disabled={!acknowledged} data-testid="mfa-finish-button" className="btn-primary mt-6 w-full">
          Continue to my dashboard
        </button>
      </div>
    );
  }

  return (
    <div data-testid="mfa-setup-page" className="container-ccc py-16 max-w-xl">
      <div className="eyebrow">Step 1 of 2 · Set up two-factor authentication</div>
      <h1 className="font-serif text-4xl mt-3">Protect your account.</h1>
      <p className="mt-4 text-ink-muted leading-relaxed">
        Because your account can access clinical letters and test results, we require a second factor at sign-in. Install an authenticator app like <strong>Google Authenticator</strong>, <strong>1Password</strong>, <strong>Microsoft Authenticator</strong> or <strong>Bitwarden</strong>, then scan this QR code.
      </p>

      {setup && (
        <>
          <div className="mt-8 p-6 bg-white border border-stone-200/60 rounded-md flex flex-col sm:flex-row items-center gap-6">
            <img src={`data:image/png;base64,${setup.qr_png_base64}`}
                 alt="MFA QR code"
                 data-testid="mfa-qr-image"
                 className="w-44 h-44 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="eyebrow">Can't scan?</div>
              <p className="mt-2 text-sm text-ink-muted">Enter this secret manually:</p>
              <code data-testid="mfa-secret-text" className="mt-2 inline-block break-all bg-cream-100 px-3 py-2 rounded-sm font-mono text-sm border border-bronze-light/40">{setup.secret_b32}</code>
            </div>
          </div>

          <form onSubmit={submit} data-testid="mfa-confirm-form" className="mt-8 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">
                Enter the 6-digit code from your app
              </label>
              <input
                required autoFocus inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                data-testid="mfa-confirm-code-input"
                className={input}
                placeholder="123456"
              />
            </div>
            {error && <div data-testid="mfa-confirm-error" className="text-sm text-destructive">{error}</div>}
            <button disabled={submitting || code.length !== 6} type="submit"
              data-testid="mfa-confirm-submit"
              className="btn-primary w-full inline-flex items-center justify-center gap-2">
              <ShieldCheck size={16}/> {submitting ? "Verifying…" : "Confirm & enable 2FA"}
            </button>
          </form>
        </>
      )}

      {error && !setup && (
        <div className="mt-6 text-sm text-destructive" data-testid="mfa-setup-error">{error}</div>
      )}
    </div>
  );
}
